/**
 * The in-app back stack used while native-app mode is on.
 *
 * ⚠️ WHY THE BROWSER'S OWN HISTORY IS NOT USED. iOS gives an installed web app an edge-swipe that
 * walks browser history, and no CSS or touch handler can switch it off. Blocking the pop afterwards
 * (the old approach) still played the swipe animation, flashed the previous page and, through the
 * overlay-close it triggered, threw away an open create/edit dialog. So in native-app mode the router
 * never ADDS a browser history entry (`routerHistory.push` is turned into a replace in
 * `src/plugins/1.router/index.ts`): with nothing behind or ahead of the current entry, the swipe has
 * nowhere to go. The previous locations live here instead, and in-app back buttons pop them through
 * `navigateBack()`.
 *
 * Mirrored to `sessionStorage` so a refresh (including the offline reload) keeps the back arrow
 * working. Session scope on purpose: a fresh launch starts with an empty stack.
 */

const STORAGE_KEY = 'alkokh:in-app-history'

/** Deep enough for any real drill-down; stops an endless detail→detail loop growing unbounded. */
const MAX_ENTRIES = 50

function readStored(): string[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []

    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : []
  }
  catch {
    return []
  }
}

const stack: string[] = typeof window === 'undefined' ? [] : readStored()

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack))
  }
  catch {
    // Private mode / quota: the stack still works for this page load.
  }
}

/** Records the location being left. Consecutive duplicates collapse into one entry. */
export function pushInAppHistory(location: string) {
  if (!location || stack[stack.length - 1] === location)
    return

  stack.push(location)

  if (stack.length > MAX_ENTRIES)
    stack.splice(0, stack.length - MAX_ENTRIES)

  persist()
}

/**
 * The location to go back to, skipping any entry equal to where we are now (a page that pushed
 * itself with a different query collapses back past its own duplicate).
 */
export function popInAppHistory(current: string): string | null {
  let entry = stack.pop()

  while (entry !== undefined && entry === current)
    entry = stack.pop()

  persist()

  return entry ?? null
}

export function clearInAppHistory() {
  if (!stack.length)
    return

  stack.length = 0
  persist()
}

export function hasInAppHistory() {
  return stack.length > 0
}
